import { useState, useEffect, useMemo } from 'react';
import { Download, FileText, Clock, Loader2, CheckCircle, ChevronDown, ChevronRight, CalendarCheck } from 'lucide-react';
import PortalLayout from '../shared/components/PortalLayout';
import EmployeeFilter from '../components/shared/EmployeeFilter';
import SearchableSelect from '../shared/components/SearchableSelect';
import ExportConfirmModal, { BelowCounterDialog } from '../components/shared/ExportConfirmModal';
import PtoMonthlyPanel from './PtoMonthlyPanel';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, navyBtn, outlineBtn, fieldStyle,
} from '../styles/tokens';
import client from '../api/client';
import { formatHours } from '../utils/formatHours';
import { formatDateSafe } from '../utils/formatDateSafe';
import { useDataMode } from '../context/DataModeContext';
import { useAuth, userIsReviewer } from '../context/AuthContext';
import { useOptions } from '../context/OptionsContext';
import { formatGlLabel } from '../utils/formatGl';
import { PAYMENT_METHODS } from '@shared/constants';

import { etiquetaProyecto } from '../utils/formatProject';
const tabs = [
  { key: 'expenses', label: 'Gastos Aprobados', icon: FileText },
  { key: 'nomina',   label: 'Nómina Aprobada',  icon: FileText },
  { key: 'exports',  label: 'Exportaciones',     icon: Download },
  // V57 — el reporte de PTO y el cierre que lo produce, juntos: para la contable son
  // el mismo momento de trabajo (revisa horas, cierra, mira la tabla que salio).
  { key: 'pto',      label: 'PTO mensual',       icon: CalendarCheck },
];

// Día LOCAL de un TIMESTAMP (timestamptz). Único uso: sage_exports.exported_at.
// `new Date(v)` + render local es lo CORRECTO acá: un timestamp es un instante y debe
// verse en la zona del que mira. NO cambiar a formatDateSafe — ése recorta a 'YYYY-MM-DD'
// y devolvería el día UTC, que al oeste de UTC puede ser el día equivocado.
// Nombre: describe lo que hace hoy — el DÍA de un timestamp, sin la hora. Si en algún
// momento se decide mostrar también la hora (ver pendiente Pieza 2), ahí pasa a ser
// formatDateTime. Antes se llamaba formatDate y servía también a columnas DATE, que era
// el origen del desfase de un día.
function formatTimestampDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-PR', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(n) {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AccountingPortal() {
  const [page, setPage] = useState('expenses');

  return (
    <PortalLayout activeModule="contabilidad">
      {/* Tab bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 0, padding: '0 20px', alignItems: 'stretch',
      }}>
        {tabs.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setPage(key)} style={{
            padding: '12px 20px', fontSize: 13,
            fontWeight: page === key ? '700' : '500',
            color: page === key ? AMBER : '#64748b',
            borderBottom: page === key ? `2px solid ${AMBER}` : '2px solid transparent',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {page === 'expenses' && <ApprovedExpenses />}
      {page === 'nomina' && <ApprovedNomina />}
      {page === 'exports' && <ExportHistory />}
      {page === 'pto' && <PtoMonthlyPanel />}
    </PortalLayout>
  );
}

/* ── Approved Expenses ─────────────────────────────────────────────────── */

function ApprovedExpenses() {
  const { mode } = useDataMode();
  const { user } = useAuth();
  const puedeAnular = userIsReviewer(user);
  const { glCodes } = useOptions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // V74 — la previa de requisiciones. `numbers` guarda lo que la persona ve en cada campo;
  // `fieldErrors` es lo que el SERVIDOR dijo de cada grupo, anclado por su groupKey.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [numbers, setNumbers] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [belowCounter, setBelowCounter] = useState(null);
  const [includeExported, setIncludeExported] = useState(false);
  const [paidFilter, setPaidFilter] = useState('unpaid');
  const [expandedReceipts, setExpandedReceipts] = useState(new Set());

  // Filters — default to no date range (show all unpaid).
  // V41: filter bar is Fecha + Vendor ID + Proyecto (replaces Método de pago + Tipo de usuario).
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sageVendorId, setSageVendorId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  // V32.4: Sage Vendor catalog for inline assignment on legacy receipts that predate
  // the requirement. Two lists; the inline dropdown picks one by the receipt's payment
  // method, and the two combined feed the V41 Vendor ID filter.
  const [creditCardVendors, setCreditCardVendors] = useState([]);
  const [pettyCashVendors, setPettyCashVendors] = useState([]);
  const [savingVendorFor, setSavingVendorFor] = useState(null);

  /**
   * Lista fusionada y deduplicada por Vendor ID para el FILTRO (las dos categorías).
   *
   * ⚠ V66 — Desde que el mismo Vendor ID puede existir en las DOS categorías, la dedup
   * dejó de ser inocua: se quedaba con el PRIMER nombre encontrado y descartaba el otro.
   * Con AMEEXP = "American Express" (tarjeta) y "Amex" (petty cash), el filtro mostraba
   * uno arbitrario —el de tarjeta, por el orden del spread— y la contable podía no
   * reconocerlo.
   *
   * Deduplicar sigue siendo correcto ACÁ: es un filtro, y filtrar por AMEEXP tiene que
   * traer los recibos de las dos categorías. Lo que se arregla es la ETIQUETA: se
   * acumulan los nombres distintos y se marca en qué categorías vive.
   *
   * ⚠ El picker de ASIGNACIÓN no necesita nada de esto y no se tocó: ya viene scopeado
   * por el `payment_method` del recibo (`isPettyCash ? pettyCashVendors : ...`), así que
   * ahí las dos filas del mismo ID NUNCA aparecen juntas. Poner la categoría también ahí
   * sería repetir en cada opción un dato que ya es constante en toda la lista.
   */
  const vendorFilterOptions = useMemo(() => {
    const byId = new Map();
    const marcar = (lista, cat) => {
      for (const v of (lista || [])) {
        if (!v?.vendor_id) continue;
        const prev = byId.get(v.vendor_id);
        if (!prev) {
          byId.set(v.vendor_id, { ...v, nombres: new Set([v.vendor_name].filter(Boolean)), cats: new Set([cat]) });
        } else {
          if (v.vendor_name) prev.nombres.add(v.vendor_name);
          prev.cats.add(cat);
        }
      }
    };
    marcar(creditCardVendors, 'Tarjeta');
    marcar(pettyCashVendors, 'Petty Cash');
    return [...byId.values()]
      .map(v => ({
        ...v,
        etiqueta: [
          v.vendor_id,
          [...v.nombres].join(' / ') || null,
          // La categoría solo se muestra cuando DISTINGUE: si el ID vive en una sola,
          // agregarla es ruido en todas las filas de la lista.
          v.cats.size > 1 ? `las dos categorías` : null,
        ].filter(Boolean).join(' — '),
      }))
      .sort((a, b) => a.vendor_id.localeCompare(b.vendor_id));
  }, [creditCardVendors, pettyCashVendors]);

  useEffect(() => {
    client.get('/api/projects').then(r => setProjects(r.data?.filter(p => p.active !== false) || [])).catch(() => {});
    client.get('/api/vendor-mappings/active', { params: { category: 'credit_card' } })
      .then(r => setCreditCardVendors(r.data || [])).catch(() => {});
    client.get('/api/vendor-mappings/active', { params: { category: 'petty_cash' } })
      .then(r => setPettyCashVendors(r.data || [])).catch(() => {});
  }, []);

  // 🔴 T-31 pieza 4 — anular. Solo Revision Final / Admin: el contable DETECTA el
  // duplicado y avisa, pero no anula — su limite (ve y exporta, no edita) es una regla no
  // negociable del proyecto, y el flujo no se traba por respetarla.
  //
  // ⚠ El gate real es del backend (403 con el motivo). Este `if` es para no mostrar un boton
  // que va a fallar: autoridad aparente, la leccion de V82.
  async function handleAnular(group) {
    const motivo = window.prompt(
      'Motivo de la anulación (requerido, queda registrado). Ej: duplicado del recibo #274, mismo archivo subido dos veces');
    if (motivo == null) return;
    if (motivo.trim().length < 10) { alert('El motivo es requerido: explicá por qué se anula.'); return; }
    // El vínculo se guarda como número, no dentro del texto del motivo: es lo que permite que
    // el badge diga "duplicado de #274" sin parsear nada.
    const dup = window.prompt(
      'Número del recibo del que es duplicado (opcional — dejalo vacío si no es un duplicado)');
    const body = { motivo: motivo.trim() };
    if (dup && /^[0-9]+$/.test(dup.trim())) body.duplicado_de = parseInt(dup.trim(), 10);
    try {
      const res = await client.post(`/api/receipts/${group.receiptId}/void`, body);
      setItems(prev => prev.map(it => it.receiptId === group.receiptId
        ? { ...it, receiptStatus: 'voided', voidedDuplicateOf: res.data.voidedDuplicateOf ?? null }
        : it));
      if (res.data.cheque_ya_emitido) {
        // 🔴 El aviso dice las DOS cosas que no son obvias: que el cheque no se deshace, y
        // que el gasto SIGUE contando en los totales. Sin la segunda, alguien concluiría que
        // el portal ya no cuadra con Sage — y es al revés: cuadra porque el void no lo saca.
        alert(`Anulado. ⚠ OJO: este recibo YA SE PAGÓ${res.data.requisiciones ? ` (requisición ${res.data.requisiciones})` : ''}. `
          + 'La anulación es solo un registro: el cheque no se deshace, y el gasto sigue contando '
          + 'en los totales porque la plata se movió de verdad.');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Error al anular el recibo.');
    }
  }

  async function handleAssignVendor(receiptId, vendorId) {
    setSavingVendorFor(receiptId);
    try {
      await client.patch(`/api/receipts/${receiptId}`, { sageVendorId: vendorId || '' });
      setItems(prev => prev.map(it => it.receiptId === receiptId ? { ...it, sageVendorId: vendorId || null } : it));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al asignar el Proveedor Sage.');
    } finally {
      setSavingVendorFor(null);
    }
  }

  function buildParams() {
    const params = { data_mode: mode };
    if (from) params.from = from;
    if (to)   params.to   = to;
    if (sageVendorId) params.sage_vendor_id = sageVendorId;
    if (projectId)    params.project_id     = projectId;
    return params;
  }

  function fetchItems() {
    setLoading(true);
    client.get('/api/accounting/receipts', { params: buildParams() })
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  // Refetch when the data mode flips (buildParams reads `mode`); also clear a now-hidden
  // project selection so the filter can't point at a project outside the current mode.
  useEffect(() => { setProjectId(''); }, [mode]);
  useEffect(() => { fetchItems(); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gastos B: batch export + mark. Confirmed via modal; on confirm we POST the list
  // of eligible receipt IDs (those with a Proveedor Sage). The server generates the
  // CSV first and, only if that succeeds, marks is_paid + is_exported + status='paid'
  // for every item of those receipts, atomically. Receipts without a Sage vendor are
  // excluded (shown in the confirmation) and never exported nor marked.
  // V74 — abrir la confirmación pide la previa de requisiciones al servidor. Antes el modal
  // se armaba solo con datos que el cliente ya tenía; ahora hay una llamada de red, y a cambio
  // un código de proyecto sin dígitos iniciales se ve ACÁ (400 que lo nombra) en vez de
  // reventar el export después.
  async function abrirConfirmacion() {
    setShowConfirm(true);
    setPreview(null); setNumbers({}); setFieldErrors({}); setGeneralError(null);
    setBelowCounter(null); setPreviewError(null); setPreviewLoading(true);
    try {
      const res = await client.post('/api/accounting/exports/expenses/preview', {
        receiptIds: eligibleGroups.map(g => g.receiptId),
        includeExported,
      });
      setPreview(res.data);
      // Precargados con lo propuesto: el caso normal es no tocar nada.
      const init = {};
      for (const g of res.data.pending || []) init[g.groupKey] = g.proposedNumber;
      setNumbers(init);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'No se pudo calcular la vista previa de requisiciones.');
    } finally {
      setPreviewLoading(false);
    }
  }

  // 🔴 Solo lo que DIFIERE de lo propuesto viaja como override. Si nadie edita, el array va
  // vacío y el request es idéntico al de antes de V74 — que es lo que mantiene el caso normal
  // en un clic. Un campo vacío cuenta como "sin editar": mandarlo daría un error de formato
  // que la persona no pidió.
  function overridesActuales() {
    return (preview?.pending || [])
      .map(g => ({ groupKey: g.groupKey, number: (numbers[g.groupKey] ?? '').trim(), propuesto: g.proposedNumber }))
      .filter(o => o.number && o.number !== o.propuesto)
      .map(({ groupKey, number }) => ({ groupKey, number }));
  }

  // Gastos B: batch export + mark. El servidor genera el CSV primero y, solo si eso sale bien,
  // marca is_paid + is_exported + status='paid' de forma atómica.
  async function doExport(allowBelowCounter = false) {
    setExporting(true);
    setFieldErrors({}); setGeneralError(null);
    try {
      const receiptIds = eligibleGroups.map(g => g.receiptId);
      const res = await client.post(
        '/api/accounting/exports/expenses',
        { receiptIds, includeExported, requisitionOverrides: overridesActuales(), allowBelowCounter },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'gastos_sage.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      setShowConfirm(false);
      setBelowCounter(null);
      fetchItems();
    } catch (err) {
      // Con responseType 'blob' el cuerpo del error viene como Blob: hay que leerlo de vuelta.
      let payload = null;
      try {
        const text = err.response?.data instanceof Blob ? await err.response.data.text() : null;
        payload = text ? JSON.parse(text) : (err.response?.data ?? null);
      } catch (_) { /* no era JSON */ }

      if (payload?.code === 'BELOW_COUNTER') {
        // El segundo paso: no una casilla, un diálogo que solo existe porque el servidor
        // acaba de decir que ese número es anterior al último emitido.
        const g = (preview?.pending || []).find(x => x.groupKey === payload.groupKey);
        setBelowCounter({
          numero: (numbers[payload.groupKey] ?? '').trim(),
          etiqueta: g ? `${g.sageVendorId || '—'} · ${g.projectCode || 'Sin proyecto'}` : null,
          ultimoEmitido: payload.extra?.ultimoEmitido,
        });
      } else if (payload?.groupKey) {
        // El resto de los rechazos se pintan bajo SU campo: el servidor devuelve groupKey
        // justamente para poder anclarlos.
        setFieldErrors({ [payload.groupKey]: payload.error });
        setBelowCounter(null);
      } else {
        setGeneralError(payload?.error || (err.response?.status === 404
          ? 'No hay recibos nuevos para exportar. Marque "Incluir exportados" para re-exportar.'
          : 'Error al exportar: ' + err.message));
        setBelowCounter(null);
      }
    } finally {
      setExporting(false);
    }
  }

  const paidCount = items.filter(i => i.isPaid).length;
  const unpaidCount = items.filter(i => !i.isPaid).length;
  const filteredItems = paidFilter === 'paid' ? items.filter(i => i.isPaid)
    : paidFilter === 'unpaid' ? items.filter(i => !i.isPaid)
    : items;
  const total = filteredItems.reduce((s, i) => {
    const amt = parseFloat(i.amount) || 0;
    return s + amt + amt * (parseFloat(i.taxRate) || 0) / 100;
  }, 0);

  // Group items by receipt
  const receiptGroups = useMemo(() => {
    const map = {};
    for (const item of filteredItems) {
      const key = item.receiptId || item.receiptNumber || item.itemId;
      if (!map[key]) {
        map[key] = {
          receiptId: item.receiptId,
          receiptNumber: item.receiptNumber,
          vendor: item.vendor,
          employeeName: item.employeeName,
          receiptDate: item.receiptDate,
          paymentMethod: item.paymentMethod,
          invoiceNumber: item.invoiceNumber,
          sageVendorId: item.sageVendorId || null,
          // T-31 pieza 4
          receiptStatus: item.receiptStatus || null,
          voidedDuplicateOf: item.voidedDuplicateOf ?? null,
          items: [],
          totalAmount: 0,
          allPaid: true,
          anyExported: false,
          anyNeedsCoding: false,   // V49: algún ítem exportable sin Proyecto/Tarea/CC/GL
        };
      }
      map[key].items.push(item);
      const amt = parseFloat(item.amount) || 0;
      map[key].totalAmount += amt + amt * (parseFloat(item.taxRate) || 0) / 100;
      if (!item.isPaid) map[key].allPaid = false;
      if (item.isExported) map[key].anyExported = true;
      if (item.needsCoding) map[key].anyNeedsCoding = true;
    }
    return Object.values(map);
  }, [filteredItems]);

  // Gastos B — export universe, computed CLIENT-SIDE from the already-filtered list
  // (honors the date/project/method/type filters AND the paid tab in memory).
  // Eligible  = has Proveedor Sage + codificación completa + (includeExported ? true : not already exported).
  // Excluded  = no Proveedor Sage (receipt-level; never exported nor marked).
  // Excluded2 = V49: le falta codificación (Proyecto/Tarea/Centro de Costo/GL) en algún ítem
  //             exportable. El recibo ENTERO se excluye a propósito: exportar solo la parte
  //             codificada emitiría un cheque corto sin avisarle a nadie. El backend además lo
  //             filtra a nivel ítem (red de seguridad si alguien fuerza por API).
  // Dedupe is by is_exported, never by is_paid.
  // 🔴 T-31 pieza 4 — el ANULADO sale del conjunto exportable POR SU ESTADO.
  //
  // ⚠⚠ Y sale tambien de `excludedGroups` ("Sin Proveedor Sage"), que es el punto del
  // ticket: el 275 quedaba ahi, **indistinguible de un recibo al que todavia no le
  // asignaron proveedor**, y era justo donde alguien podia asignarle uno creyendo que era
  // un pendiente normal. Un anulado no es un pendiente de nada.
  const anulado = (g) => g.receiptStatus === 'voided';
  const eligibleGroups = useMemo(
    () => receiptGroups.filter(g => !anulado(g) && g.sageVendorId && !g.anyNeedsCoding && (includeExported || !g.anyExported)),
    [receiptGroups, includeExported],
  );
  const excludedGroups = useMemo(
    () => receiptGroups.filter(g => !anulado(g) && !g.sageVendorId),
    [receiptGroups],
  );
  const voidedGroups = useMemo(() => receiptGroups.filter(anulado), [receiptGroups]);
  const uncodedGroups = useMemo(
    () => receiptGroups.filter(g => g.sageVendorId && g.anyNeedsCoding),
    [receiptGroups],
  );
  // V74 — El resumen POR PROYECTO se retiro: lo reemplaza el de REQUISICIONES, que sale de
  // la previa del servidor. No se pierde el corte por proyecto (cada requisicion ES un
  // proyecto y un proveedor) y ademas se gana lo que antes no se veia: que items comparten
  // cheque. Tener los dos resumenes seria ruido.

  function toggleExpand(receiptId) {
    setExpandedReceipts(prev => {
      const next = new Set(prev);
      next.has(receiptId) ? next.delete(receiptId) : next.add(receiptId);
      return next;
    });
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 16px' }}>Gastos Aprobados</h2>

      {/* Filter bar */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Desde</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Hasta</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Vendor ID</div>
          <select value={sageVendorId} onChange={e => setSageVendorId(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 180 }}>
            <option value="">Todos</option>
            {vendorFilterOptions.map(v => (
              <option key={v.vendor_id} value={v.vendor_id}>{v.etiqueta}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Proyecto</div>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 160 }}>
            <option value="">Todos</option>
            {projects
              .filter(p => mode === 'testing' ? p.is_testing === true : !p.is_testing)
              .map(p => <option key={p.id} value={p.id}>{etiquetaProyecto(p.code, p.name)}</option>)}
          </select>
        </div>
        <button onClick={fetchItems} disabled={loading} style={{ ...navyBtn, padding: '7px 16px', fontSize: 12 }}>
          {loading ? 'Cargando…' : 'Buscar'}
        </button>
      </div>

      {/* Summary + actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <strong style={{ color: NAVY }}>{receiptGroups.length}</strong> recibos ·{' '}
          <strong style={{ color: NAVY }}>{filteredItems.length}</strong> items ·{' '}
          <strong style={{ color: NAVY }}>{formatCurrency(total)}</strong> total ·{' '}
          {paidCount} pagados · {unpaidCount} pendientes
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {['all', 'unpaid', 'paid'].map(val => (
            <button key={val} onClick={() => setPaidFilter(val)} style={{
              padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: '600', cursor: 'pointer',
              border: `1px solid ${paidFilter === val ? AMBER : BORDER_COLOR}`,
              background: paidFilter === val ? '#fffbeb' : '#fff',
              color: paidFilter === val ? '#92400e' : '#64748b',
            }}>
              {val === 'all' ? 'Todos' : val === 'paid' ? 'Pagados' : 'Sin Pagar'}
            </button>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeExported} onChange={e => setIncludeExported(e.target.checked)} />
            Incluir exportados
          </label>
          <button onClick={abrirConfirmacion} disabled={exporting || eligibleGroups.length === 0} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            opacity: exporting || eligibleGroups.length === 0 ? 0.5 : 1,
          }}>
            <Download size={14} /> {exporting ? 'Exportando…' : 'Exportar'}
          </button>
        </div>
      </div>

      <div style={bubbleStyle}>
        {/* Header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '28px 50px 1fr 1fr 100px 100px 90px 80px 80px',
          gap: 6, padding: '0 0 12px', borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: 10, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span></span><span>#</span><span>Proveedor</span><span>Empleado</span>
          <span>Método</span><span>Fecha</span><span style={{ textAlign: 'right' }}>Total</span>
          <span style={{ textAlign: 'center' }}>Pagado</span><span></span>
        </div>

        {receiptGroups.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No hay recibos aprobados {paidFilter === 'paid' ? 'pagados' : paidFilter === 'unpaid' ? 'sin pagar' : ''}
          </div>
        )}

        {receiptGroups.map(group => {
          const isExpanded = expandedReceipts.has(group.receiptId);
          const ExpandIcon = isExpanded ? ChevronDown : ChevronRight;
          return (
            <div key={group.receiptId}>
              {/* Receipt summary row */}
              <div
                onClick={() => toggleExpand(group.receiptId)}
                style={{
                  display: 'grid', gridTemplateColumns: '28px 50px 1fr 1fr 100px 100px 90px 80px 80px',
                  gap: 6, padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
                  alignItems: 'center', fontSize: 13, cursor: 'pointer',
                  background: group.allPaid ? '#f0fdf4' : '#fff',
                }}
              >
                <ExpandIcon size={16} style={{ color: '#94a3b8' }} />
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{group.receiptNumber || '—'}</span>
                <div>
                  <div style={{ fontWeight: '600', color: NAVY, fontSize: 13 }}>{group.vendor || '—'}</div>
                  {group.invoiceNumber && <div style={{ fontSize: 11, color: '#94a3b8' }}>Fact: {group.invoiceNumber}</div>}
                  {!group.sageVendorId && !group.anyExported && (
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#92400e' }}>⚠ Sin Proveedor Sage</div>
                  )}
                </div>
                <span style={{ color: '#475569', fontSize: 12 }}>{group.employeeName || '—'}</span>
                <span style={{ color: '#475569', fontSize: 11 }}>
                  {group.paymentMethod ? (PAYMENT_METHODS[group.paymentMethod] || group.paymentMethod) : '—'}
                </span>
                <span style={{ color: '#475569', fontSize: 12 }}>{formatDateSafe(group.receiptDate)}</span>
                <span style={{ textAlign: 'right', fontWeight: '700', color: NAVY }}>
                  {formatCurrency(group.totalAmount)}
                  <div style={{ fontSize: 10, fontWeight: '400', color: '#94a3b8' }}>{group.items.length} item{group.items.length !== 1 ? 's' : ''}</div>
                </span>
                <div style={{ textAlign: 'center' }}>
                  {group.allPaid ? (
                    <CheckCircle size={16} style={{ color: '#16a34a' }} />
                  ) : (
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>
                      {group.items.filter(i => i.isPaid).length}/{group.items.length}
                    </span>
                  )}
                </div>
                <div style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                  {group.anyExported && (
                    <span style={{
                      padding: '3px 8px', borderRadius: 6, fontSize: 9, fontWeight: '600',
                      background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                    }}>Exportado</span>
                  )}
                </div>
              </div>

              {/* Expanded items — matches export columns */}
              {isExpanded && (
                <div style={{ background: '#f8fafc', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  {/* V32.4: Sage Vendor assignment. Legacy receipts approved before the
                      requirement have no vendor and won't export until one is assigned.
                      Locked once any item is exported. */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '8px 16px', borderBottom: `1px solid ${BORDER_COLOR}`,
                    background: (group.sageVendorId || group.anyExported) ? '#f8fafc' : '#fffbeb',
                  }} onClick={e => e.stopPropagation()}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, whiteSpace: 'nowrap' }}>Proveedor Sage:</span>
                    {/* 🔴 T-31 pieza 4 — LOS DOS BADGES SON DISTINTOS, Y NO ES CosmETICA.
                        "Anulado" a secas sobre un recibo PAGADO se lee como "no paso nada", que
                        es lo OPUESTO a la verdad: el cheque salio. Por eso el segundo badge
                        lleva el aviso, y no una nota al pie. */}
                    {group.receiptStatus === 'voided' ? (
                      <span style={{
                        background: (group.anyExported || group.allPaid) ? '#fee2e2' : '#e2e8f0',
                        color: (group.anyExported || group.allPaid) ? '#b91c1c' : '#334155',
                        border: `1px solid ${(group.anyExported || group.allPaid) ? '#fca5a5' : '#cbd5e1'}`,
                        borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
                      }}>
                        Anulado
                        {group.voidedDuplicateOf ? ` — duplicado de #${group.voidedDuplicateOf}` : ''}
                        {(group.anyExported || group.allPaid) ? ' · ⚠ EL CHEQUE YA SALIO' : ''}
                      </span>
                    ) : group.anyExported ? (
                      <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, fontFamily: 'monospace' }}>
                        ✓ {group.sageVendorId || '—'} (exportado, bloqueado)
                      </span>
                    ) : (() => {
                      const isPettyCash = group.paymentMethod === 'petty_cash' || group.paymentMethod === 'efectivo';
                      const vendorItems = ((isPettyCash ? pettyCashVendors : creditCardVendors) || [])
                        .map(v => ({ id: v.vendor_id, label: v.vendor_id, sublabel: v.vendor_name }));
                      return (
                        <>
                          <div style={{ width: 300 }}>
                            <SearchableSelect
                              items={vendorItems}
                              value={group.sageVendorId}
                              onChange={(vid) => handleAssignVendor(group.receiptId, vid)}
                              placeholder={isPettyCash ? 'Buscar PET-...' : 'Buscar proveedor...'}
                              disabled={savingVendorFor === group.receiptId}
                            />
                          </div>
                          {group.sageVendorId
                            ? <span style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>✓ {group.sageVendorId}</span>
                            : <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>⚠ Sin Proveedor Sage — no exportará</span>
                          }
                          {savingVendorFor === group.receiptId && <span style={{ fontSize: 11, color: '#94a3b8' }}>Guardando…</span>}
                          {puedeAnular && (
                            <button onClick={() => handleAnular(group)}
                              title="Anular este recibo (queda registrado con motivo)"
                              style={{ fontSize: 11, color: '#b91c1c', background: 'none', border: 'none',
                                       cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 600 }}>
                              Anular
                            </button>
                          )}
                          {group.sageVendorId && savingVendorFor !== group.receiptId && (
                            <button onClick={() => handleAssignVendor(group.receiptId, null)}
                              style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                              Quitar
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {/* Sub-header */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 90px 110px 90px 70px 35px 65px 45px 55px 65px 70px 70px',
                    gap: 4, padding: '6px 16px', fontSize: 9, fontWeight: 700, color: '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${BORDER_COLOR}`,
                  }}>
                    <span>Descripción</span><span>Proyecto ID</span><span>Tarea</span><span>Centro Costo</span>
                    <span>GL</span>
                    <span style={{ textAlign: 'center' }}>Cant</span>
                    <span style={{ textAlign: 'right' }}>Monto</span>
                    <span style={{ textAlign: 'right' }}>Tax%</span>
                    <span style={{ textAlign: 'right' }}>Tax$</span>
                    <span style={{ textAlign: 'right' }}>Total</span>
                    <span style={{ textAlign: 'center' }}>Pagado</span><span></span>
                  </div>
                  {group.items.map(item => {
                    const amt = parseFloat(item.amount) || 0;
                    const taxAmt = amt * (parseFloat(item.taxRate) || 0) / 100;
                    return (
                    <div key={item.itemId} style={{
                      display: 'grid', gridTemplateColumns: '1fr 90px 110px 90px 70px 35px 65px 45px 55px 65px 70px 70px',
                      gap: 4, padding: '7px 16px', borderBottom: `1px solid #f1f5f9`,
                      alignItems: 'start', fontSize: 11,
                    }}>
                      <div style={{ minWidth: 0, wordBreak: 'break-word' }}>
                        <span style={{ fontWeight: '500', color: '#475569' }}>{item.description || '—'}</span>
                        {item.isPersonal && (
                          <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 12, padding: '1px 6px', fontSize: 9, fontWeight: 600, marginLeft: 6 }}>Personal</span>
                        )}
                      </div>
                      <span style={{ color: '#475569', fontSize: 10, wordBreak: 'break-word' }}>{item.projectCode || '—'}</span>
                      <span style={{ color: '#475569', fontSize: 10, wordBreak: 'break-word' }}>{item.selectedTaskName || '—'}</span>
                      <span style={{ color: '#475569', fontSize: 10, wordBreak: 'break-word' }}>{item.costCenter || '—'}</span>
                      <span style={{ color: '#475569', fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-word' }}>{item.glCode ? formatGlLabel(item.glCode, glCodes) : '—'}</span>
                      <span style={{ textAlign: 'center', color: '#475569' }}>{item.quantity || 1}</span>
                      <span style={{ textAlign: 'right', fontWeight: '600', color: '#475569' }}>{formatCurrency(amt / (item.quantity || 1))}</span>
                      <span style={{ textAlign: 'right', color: '#94a3b8' }}>{item.taxRate > 0 ? `${item.taxRate}%` : '0%'}</span>
                      <span style={{ textAlign: 'right', color: '#94a3b8' }}>{taxAmt > 0 ? formatCurrency(taxAmt) : '—'}</span>
                      <span style={{ textAlign: 'right', fontWeight: '600', color: NAVY }}>{formatCurrency(amt + taxAmt)}</span>
                      <div style={{ textAlign: 'center' }}>
                        {item.isPaid ? <CheckCircle size={14} style={{ color: '#16a34a' }} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        {item.isExported && (
                          <span style={{ fontSize: 9, fontWeight: 600, color: '#16a34a' }}>✓ Exp.</span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showConfirm && (
        <ExportConfirmModal
          loading={previewLoading}
          previewError={previewError}
          pending={preview?.pending || []}
          assigned={preview?.assigned || []}
          receiptCount={preview?.receiptCount || 0}
          numbers={numbers}
          onChangeNumber={(k, v) => {
            setNumbers(prev => ({ ...prev, [k]: v }));
            // Un error del servidor deja de valer en cuanto la persona cambia lo que lo causo.
            setFieldErrors(prev => (prev[k] ? { ...prev, [k]: undefined } : prev));
          }}
          fieldErrors={fieldErrors}
          generalError={generalError}
          excludedGroups={excludedGroups}
          uncodedGroups={uncodedGroups}
          includeExported={includeExported}
          exporting={exporting}
          onConfirm={() => doExport(false)}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      <BelowCounterDialog
        pedido={belowCounter}
        exporting={exporting}
        onAccept={() => doExport(true)}
        onCancel={() => setBelowCounter(null)}
      />
    </div>
  );
}

/* ── Nómina Sheet List (Collapsible) ───────────────────────────────────── */

// V31: derive sheet-level export state from entry-level tracking returned by getApprovedSheets.
const SAGE_BADGE_CONFIG = {
  exported: { label: 'Exportado', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  partial:  { label: 'Parcial',   color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  pending:  { label: 'Pendiente', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
};

function getSheetExportStatus(sheet) {
  const total = Number(sheet.total_entries) || 0;
  const exported = Number(sheet.exported_entries) || 0;
  if (total === 0 || exported === 0) return 'pending';
  if (exported === total) return 'exported';
  return 'partial';
}

function NominaSheetList({ sheets }) {
  const [expanded, setExpanded] = useState({});

  function toggleSheet(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  // Group entries by employee for display
  function groupByEmployee(entries) {
    const map = {};
    for (const e of (entries || [])) {
      const key = e.employee_id || e.employee_name || 'unknown';
      if (!map[key]) map[key] = { name: e.employee_name || '—', id: e.employee_id || '—', tasks: [] };
      map[key].tasks.push(e);
    }
    return Object.values(map);
  }

  return (
    <div style={bubbleStyle}>
      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: '24px 1fr 100px 1fr 70px 70px 60px 70px 80px',
        gap: 6, padding: '0 0 10px', borderBottom: `1px solid ${BORDER_COLOR}`,
        fontSize: 10, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        <span></span>
        <span>Proyecto</span><span>Fecha</span><span>Supervisor</span>
        <span style={{ textAlign: 'right' }}>Regular</span>
        <span style={{ textAlign: 'right' }}>Extra</span>
        <span style={{ textAlign: 'center' }}>Empl.</span>
        <span>Estado</span>
        <span style={{ textAlign: 'center' }}>Sage</span>
      </div>

      {sheets.length === 0 && (
        <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          No hay tarjetas de nómina aprobadas pendientes de exportación
        </div>
      )}

      {sheets.map(s => {
        const isOpen = !!expanded[s.id];
        const empGroups = isOpen ? groupByEmployee(s.entries) : [];

        return (
          <div key={s.id} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
            {/* Sheet row — clickable */}
            <div
              onClick={() => toggleSheet(s.id)}
              style={{
                display: 'grid', gridTemplateColumns: '24px 1fr 100px 1fr 70px 70px 60px 70px 80px',
                gap: 6, padding: '10px 0', alignItems: 'center', fontSize: 13,
                cursor: 'pointer', transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isOpen ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#64748b" />}
              </span>
              <span style={{ fontWeight: '600', color: NAVY, fontSize: 12 }}>
                {etiquetaProyecto(s.project_code, s.project_name, { sinNombre: '—' })}
              </span>
              <span style={{ color: '#475569', fontSize: 12 }}>{formatDateSafe(s.sheet_date)}</span>
              <span style={{ color: '#475569', fontSize: 12 }}>{s.supervisor_name || '—'}</span>
              <span style={{ textAlign: 'right', fontWeight: '600', fontSize: 12 }}>{formatHours(s.total_regular)}h</span>
              <span style={{ textAlign: 'right', fontWeight: '600', color: AMBER, fontSize: 12 }}>{formatHours(s.total_overtime)}h</span>
              <span style={{ textAlign: 'center', fontSize: 12, color: '#475569' }}>{s.employee_count || '—'}</span>
              <span>
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 20,
                  fontSize: 9, fontWeight: '600', background: '#f0fdf4', color: '#166534',
                }}>Aprobado</span>
              </span>
              <span style={{ textAlign: 'center' }}>
                {(() => {
                  const status = getSheetExportStatus(s);
                  const cfg = SAGE_BADGE_CONFIG[status];
                  const total = Number(s.total_entries) || 0;
                  const exported = Number(s.exported_entries) || 0;
                  return (
                    <span
                      title={status === 'partial' ? `${exported} de ${total} entries exportadas` : undefined}
                      style={{
                        display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                        fontSize: 9, fontWeight: '600',
                        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                      }}
                    >
                      {cfg.label}
                    </span>
                  );
                })()}
              </span>
            </div>

            {/* Expanded: entry details grouped by employee */}
            {isOpen && (
              <div style={{
                padding: '0 0 12px 32px',
                animation: 'fadeUp 0.15s ease',
              }}>
                {empGroups.map((emp, ei) => (
                  <div key={ei} style={{
                    marginBottom: ei < empGroups.length - 1 ? 10 : 0,
                    background: BG_FIELD, borderRadius: 8, padding: '8px 12px',
                  }}>
                    {/* Employee header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                      fontSize: 12, fontWeight: 600, color: NAVY,
                    }}>
                      <span style={{
                        background: '#dbeafe', color: '#1e40af', padding: '1px 6px',
                        borderRadius: 8, fontSize: 10, fontWeight: 700,
                      }}>ID: {emp.id}</span>
                      {emp.name}
                    </div>

                    {/* Task rows */}
                    <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1fr', gap: 4, fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>
                      <span>Tarea</span><span>Descripción</span><span>C. Costo</span>
                      <span style={{ textAlign: 'right' }}>Reg</span>
                      <span style={{ textAlign: 'right' }}>Extra</span>
                      <span>Notas</span>
                    </div>
                    {emp.tasks.map((t, ti) => {
                      // V31.1: preview mirrors the CSV's strict whitelist — only the literal
                      // '02-FRINGES' renders as fringe. Any other category value (including
                      // typos) shows as '01-LABOR' so accounting sees exactly what would land
                      // in Sage.
                      const isFringe = String(t.cost_code_category || '').trim() === '02-FRINGES';
                      const cc = isFringe ? '02-FRINGES' : '01-LABOR';
                      return (
                        <div key={ti} style={{
                          display: 'grid', gridTemplateColumns: '100px 1fr 80px 60px 60px 1fr',
                          gap: 4, fontSize: 12, padding: '3px 0',
                          borderTop: ti > 0 ? `1px solid ${BORDER_COLOR}` : 'none',
                        }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: NAVY }}>{t.cost_code || '—'}</span>
                          <span style={{ color: '#475569' }}>{t.cost_code_desc || '—'}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            color: isFringe ? '#7c3aed' : '#166534',
                          }}>{cc}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatHours(t.regular_hours)}</span>
                          <span style={{ textAlign: 'right', fontWeight: 600, color: AMBER }}>{formatHours(t.overtime_hours)}</span>
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>{t.notes || ''}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Approved Nómina ───────────────────────────────────────────────────── */

function ApprovedNomina() {
  const { mode } = useDataMode();
  const [sheets, setSheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeExported, setIncludeExported] = useState(false);

  // Filters
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [projectId, setProjectId] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]); // [] = all

  useEffect(() => {
    client.get('/api/projects').then(r => setProjects(r.data?.filter(p => p.active !== false) || [])).catch(() => {});
    client.get('/api/nomina/workers').then(r => setEmployees(r.data || [])).catch(() => {});
    fetchSheets();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the data mode flips; clear a now-hidden project selection.
  useEffect(() => { setProjectId(''); }, [mode]);
  useEffect(() => { fetchSheets(); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  function fetchSheets() {
    setLoading(true);
    const params = { data_mode: mode };
    if (from)      params.from       = from;
    if (to)        params.to         = to;
    if (projectId) params.project_id = projectId;
    client.get('/api/accounting/nomina', { params })
      .then(r => setSheets(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = { data_mode: mode };
      if (includeExported) params.includeExported = 'true';
      if (from)      params.from       = from;
      if (to)        params.to         = to;
      if (projectId) params.project_id = projectId;
      if (selectedEmployees.length) params.employee_ids = selectedEmployees.join(',');
      const res = await client.get('/api/accounting/exports/nomina', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || 'nomina_sage.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      // Admin (GL-coded) entries are now included in the Sage file as {project},{gl},
      // so the old "GL→Sage pending" exclusion notice is gone. Data-integrity notice:
      // entries with NEITHER tarea/CC nor GL are skipped and surfaced here.
      const skippedUncoded = parseInt(res.headers['x-skipped-uncoded-count'] || '0', 10);
      if (skippedUncoded > 0) {
        alert(`Nota: ${skippedUncoded} entrada(s) sin código se omitieron del archivo Sage — requieren una Tarea/Centro de Costo o una Cuenta GL.`);
      }
      // Refetch so the badges reflect the new sage_exported_at marks.
      fetchSheets();
    } catch (err) {
      // responseType is 'blob', so a JSON error body arrives as a Blob — read it back
      // to surface the server's message (e.g. the "nothing new to export" 404).
      let serverMsg = '';
      if (err.response?.data instanceof Blob) {
        try { serverMsg = JSON.parse(await err.response.data.text()).error; } catch { /* not JSON */ }
      } else {
        serverMsg = err.response?.data?.error;
      }
      const msg = serverMsg
        ? serverMsg
        : (err.response?.status === 404
            ? 'No hay registros nuevos para exportar. Marque "Incluir exportados" para re-exportar.'
            : 'Error al exportar: ' + err.message);
      alert(msg);
    } finally {
      setExporting(false);
    }
  }

  const totalRegular  = sheets.reduce((s, r) => s + parseFloat(r.total_regular  || 0), 0);
  const totalOvertime = sheets.reduce((s, r) => s + parseFloat(r.total_overtime || 0), 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 16px' }}>Nómina Aprobada</h2>

      {/* Filter bar */}
      <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Desde</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Hasta</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Proyecto</div>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}
            style={{ ...fieldStyle, padding: '5px 8px', fontSize: 12, width: 'auto', minWidth: 180 }}>
            <option value="">Todos los proyectos</option>
            {projects
              .filter(p => mode === 'testing' ? p.is_testing === true : !p.is_testing)
              .map(p => (
                <option key={p.id} value={p.id}>{etiquetaProyecto(p.code, p.name)}</option>
              ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Empleados</div>
          <EmployeeFilter
            employees={employees}
            selected={selectedEmployees}
            onChange={setSelectedEmployees}
          />
        </div>
        <button onClick={fetchSheets} disabled={loading} style={{ ...navyBtn, padding: '7px 16px', fontSize: 12 }}>
          {loading ? 'Cargando…' : 'Buscar'}
        </button>
      </div>

      {/* Summary + export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <strong style={{ color: NAVY }}>{sheets.length}</strong> tarjetas ·{' '}
          <strong style={{ color: NAVY }}>{formatHours(totalRegular)}h</strong> regular ·{' '}
          <strong style={{ color: AMBER }}>{formatHours(totalOvertime)}h</strong> extra
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeExported} onChange={e => setIncludeExported(e.target.checked)} />
            Incluir exportados
          </label>
          <button onClick={handleExport} disabled={exporting || sheets.length === 0} style={{
            ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
            opacity: exporting || sheets.length === 0 ? 0.5 : 1,
          }}>
            <Download size={14} /> {exporting ? 'Exportando…' : 'Exportar CSV (Sage)'}
          </button>
        </div>
      </div>

      <NominaSheetList sheets={sheets} />
    </div>
  );
}

/* ── Export History ─────────────────────────────────────────────────────── */

function ExportHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/accounting/exports/history')
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0, marginBottom: 20 }}>
        Historial de Exportaciones
      </h2>

      <div style={bubbleStyle}>
        <div style={{
          display: 'grid', gridTemplateColumns: '120px 1fr 100px 1fr',
          gap: 8, padding: '0 0 12px', borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span>Módulo</span><span>Archivo</span><span style={{ textAlign: 'right' }}>Registros</span><span>Exportado</span>
        </div>

        {history.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No hay exportaciones previas
          </div>
        )}

        {history.map(h => (
          <div key={h.id} style={{
            display: 'grid', gridTemplateColumns: '120px 1fr 100px 1fr',
            gap: 8, padding: '12px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            alignItems: 'center', fontSize: 13,
          }}>
            <span>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                fontSize: 10, fontWeight: '600',
                background: h.module === 'expenses' ? '#dbeafe' : '#fef3c7',
                color: h.module === 'expenses' ? '#1e40af' : '#92400e',
              }}>
                {h.module === 'expenses' ? 'Gastos' : 'Nómina'}
              </span>
            </span>
            {/* V31: nomina_entries rows track entry IDs in record_ids (vs nomina = sheet IDs) — display the same label. */}
            <span style={{ fontWeight: '500', color: NAVY }}>{h.file_name || '—'}</span>
            <span style={{ textAlign: 'right', fontWeight: '600' }}>{h.row_count}</span>
            <span style={{ color: '#475569' }}>
              {formatTimestampDate(h.exported_at)} — {h.exported_by_name || '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
      <Loader2 size={20} className="spin" /> Cargando…
    </div>
  );
}
